import { useState } from 'react'
import { Box, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import { Lang, SUPPORTED_LANGS } from '.'
import { useTranslation } from './I18nProvider'

interface FlagProps {
  size?: number
}

function FlagBR({ size = 22 }: FlagProps) {
  return (
    <svg width={size} height={size * (14 / 20)} viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="20" height="14" rx="2" fill="#009C3B" />
      <polygon points="10,2 18,7 10,12 2,7" fill="#FFDF00" />
      <circle cx="10" cy="7" r="2.7" fill="#002776" />
      <path d="M7.6 7.4 Q10 6.2 12.4 7.4" stroke="#fff" strokeWidth="0.5" fill="none" />
    </svg>
  )
}

function FlagUS({ size = 22 }: FlagProps) {
  return (
    <svg width={size} height={size * (14 / 20)} viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="20" height="14" rx="2" fill="#B22234" />
      {[1, 3, 5, 7, 9, 11].map((y) => (
        <rect key={y} x="0" y={y} width="20" height="1" fill="#fff" />
      ))}
      <rect width="9" height="7" fill="#3C3B6E" />
      {[
        [1.5, 1.5], [4.5, 1.5], [7.5, 1.5],
        [3, 3], [6, 3],
        [1.5, 4.5], [4.5, 4.5], [7.5, 4.5],
      ].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r="0.5" fill="#fff" />
      ))}
    </svg>
  )
}

function FlagES({ size = 22 }: FlagProps) {
  return (
    <svg width={size} height={size * (14 / 20)} viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect width="20" height="14" rx="2" fill="#AA151B" />
      <rect y="3.5" width="20" height="7" fill="#F1BF00" />
      <rect x="3.5" y="5" width="1.5" height="4" fill="#AA151B" rx="0.2" />
    </svg>
  )
}

const FLAGS: Record<Lang, React.ComponentType<FlagProps>> = {
  pt: FlagBR,
  en: FlagUS,
  es: FlagES,
}

/**
 * Floating language switcher, fixed at the top-right of the viewport.
 *
 * The link-venda-direta layout splits each page into left/right sections
 * configured via settings.json; we don't want to require every brand to
 * add a switcher entry to its config, and we don't want to disturb the
 * existing logo/heading layout. A fixed floating pill works on every
 * page out of the box and is the convention global sites use.
 */
export function LanguageSwitcher() {
  const { lang, setLang, t } = useTranslation()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const CurrentFlag = FLAGS[lang]
  const open = Boolean(anchor)

  function handleSelect(next: Lang) {
    setLang(next)
    setAnchor(null)
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchor(e.currentTarget as HTMLElement)}
        aria-label={t.languageSwitcher.label}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          position: 'fixed',
          top: { xs: 12, md: 20 },
          right: { xs: 12, md: 24 },
          zIndex: 2000,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          background: 'rgba(255,255,255,0.92)',
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '999px',
          cursor: 'pointer',
          padding: '6px 12px',
          minHeight: 36,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            background: 'rgba(255,255,255,1)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
          },
        }}
      >
        <CurrentFlag size={22} />
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
          <path
            d="M2 4l3 3 3-3"
            stroke="#4A4A4A"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, borderRadius: '12px', minWidth: 160 } }}
      >
        {SUPPORTED_LANGS.map((l) => {
          const Flag = FLAGS[l]
          const label = t.languageSwitcher[l]
          return (
            <MenuItem
              key={l}
              selected={l === lang}
              onClick={() => handleSelect(l)}
              sx={{ gap: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Flag size={22} />
              </ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
